import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import api from "services/api";

interface DocuSignClickwrap {
  Clickwrap: {
    render: (config: any, containerId: string) => void;
    on: (event: string, handler: (data: any) => void) => void;
  };
}

declare global {
  interface Window {
    docuSignClick?: DocuSignClickwrap;
  }
}

interface ClickwrapData {
  clickwrapId: string;
  clickwrapName: string;
  fileType: string;
  status: string;
  accountId: string;
  versionNumber: string;
  createdTime?: string;
  lastModified?: string;
}

interface NewUnitDataProps {
  show?: boolean;
  clientUserId?: string;
  handleAccepted?: () => void;
  unityId?: string;
}

const TermoAdesao = ({
  show: externalShow,
  clientUserId: customClientUserId,
  handleAccepted,
  unityId,
}: NewUnitDataProps): JSX.Element => {
  const [showModal, setShowModal] = useState(false);
  const [clickwrap, setClickwrap] = useState<ClickwrapData | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [docusignConfig, setDocusignConfig] = useState<{
    environment: string;
    accountId: string;
  } | null>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const renderAttemptedRef = useRef(false);
  const agreedHandledRef = useRef(false);

  const clickwrapIdRef = useRef<string | null>(null);

  useEffect(() => {
    const loadDocuSignScript = (): Promise<void> => {
      return new Promise((resolve, reject) => {
        if (window.docuSignClick?.Clickwrap) {
          setScriptLoaded(true);
          resolve();
          return;
        }

        const isProduction =
          process.env.REACT_APP_ENV === "build-prod" ||
          process.env.REACT_APP_ENV === "prod";

        const scriptUrl = isProduction
          ? "https://na2.docusign.net/clickapi/sdk/latest/docusign-click.js"
          : "https://demo.docusign.net/clickapi/sdk/latest/docusign-click.js";

        const existingScript = document.querySelector(
          `script[src="${scriptUrl}"]`
        );

        if (existingScript) {
          const checkInterval = setInterval(() => {
            if (window.docuSignClick?.Clickwrap) {
              clearInterval(checkInterval);
              setScriptLoaded(true);
              resolve();
            }
          }, 100);

          setTimeout(() => {
            clearInterval(checkInterval);
            if (!window.docuSignClick?.Clickwrap) {
              reject(new Error("Script DocuSign não carregou a tempo"));
            }
          }, 10000);

          return;
        }

        const script = document.createElement("script");
        script.src = scriptUrl;
        script.async = true;
        script.onload = () => {
          const checkInterval = setInterval(() => {
            if (window.docuSignClick?.Clickwrap) {
              clearInterval(checkInterval);
              setScriptLoaded(true);
              resolve();
            }
          }, 100);

          setTimeout(() => {
            clearInterval(checkInterval);
            if (!window.docuSignClick?.Clickwrap) {
              reject(new Error("SDK DocuSign não inicializou após carregar"));
            }
          }, 10000);
        };

        script.onerror = () => {
          reject(new Error(`Erro ao carregar script DocuSign: ${scriptUrl}`));
        };

        document.head.appendChild(script);
      });
    };

    loadDocuSignScript()
      .then(() => {
        (async () => {
          await Promise.all([fetchDocusignConfig(), loadClickwrap()]);
          setShouldRender(true);
        })();
      })
      .catch((error) => {
        toast.error(
          error?.message ||
            "Erro ao carregar SDK DocuSign. Recarregue a página."
        );
      });
  }, []);

  useEffect(() => {
    // O prop externo só força a visibilidade do modal; não bloqueia inicialização
    if (externalShow === true) {
      setShowModal(true);
    }
    if (externalShow === false) {
      setShowModal(false);
    }
  }, [externalShow]);

  useEffect(() => {
    const shouldInitialize =
      shouldRender &&
      scriptLoaded &&
      clickwrap &&
      docusignConfig &&
      !isInitialized &&
      clickwrap.clickwrapId !== clickwrapIdRef.current;

    if (shouldInitialize && !renderAttemptedRef.current) {
      renderAttemptedRef.current = true;
      clickwrapIdRef.current = clickwrap.clickwrapId;

      let retryCount = 0;
      const maxRetries = 20;
      const checkSDKAndInitialize = (): void => {
        if (window.docuSignClick?.Clickwrap) {
          setTimeout(() => {
            initializeDocuSign();
          }, 200);
        } else if (retryCount < maxRetries) {
          retryCount += 1;
          setTimeout(checkSDKAndInitialize, 100);
        } else {
          renderAttemptedRef.current = false;
          toast.error(
            "SDK DocuSign não carregou a tempo. Por favor, recarregue a página."
          );
        }
      };

      const timer = setTimeout(() => {
        checkSDKAndInitialize();
      }, 300);

      return () => {
        clearTimeout(timer);
        retryCount = maxRetries; // Para as tentativas ao desmontar
      };
    }

    if (!shouldRender && !showModal) {
      cleanupDocuSign();
      setIsInitialized(false);
      renderAttemptedRef.current = false;
      clickwrapIdRef.current = null;
    }

    return undefined;
  }, [
    shouldRender,
    scriptLoaded,
    clickwrap,
    docusignConfig,
    isInitialized,
    showModal,
  ]);

  // Fallback: ouve mensagens do iframe do DocuSign e tenta detectar aceite
  useEffect(() => {
    if (!showModal) {
      return undefined;
    }
    const onMessage = (event: MessageEvent): void => {
      try {
        const { data }: { data: any } = event;
        const payloadText =
          typeof data === "string" ? data : JSON.stringify(data || {});
        const text = (payloadText || "").toLowerCase();
        const looksLikeAgreed =
          text.includes("clickwrap") &&
          (text.includes("agreed") ||
            text.includes("agreementaccepted") ||
            text.includes("accepted"));
        if (looksLikeAgreed) {
          handleAgreed();
        }
      } catch {
        // ignore
      }
    };
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
    };
  }, [showModal]);

  const cleanupDocuSign = (): void => {
    try {
      // Limpa apenas o conteúdo do container
      const container = document.getElementById("docusign-clickwrap-container");
      if (container) {
        container.innerHTML = "";
      }

      // Reseta o estado de aceite
      agreedHandledRef.current = false;
    } catch (error) {
      console.warn("Erro ao limpar DocuSign:", error);
    }
  };

  const fetchDocusignConfig = async (): Promise<void> => {
    try {
      const headers = {
        access_token: localStorage.getItem("@UltragazEmpresarial:accessToken"),
      };

      const { data } = await api.get(
        "/industrial/customer/v1/clickwraps/config",
        { headers }
      );
      if (data?.environment && data?.accountId) {
        setDocusignConfig({
          environment: data.environment,
          accountId: data.accountId,
        });
      }
    } catch (err: any) {
      // ignore
    }
  };

  const compareVersions = (v1: string, v2: string): number => {
    const parts1 = v1.split(".").map(Number);
    const parts2 = v2.split(".").map(Number);
    const maxLength = Math.max(parts1.length, parts2.length);

    for (let i = 0; i < maxLength; i += 1) {
      const part1 = parts1[i] || 0;
      const part2 = parts2[i] || 0;

      if (part1 > part2) return 1;
      if (part1 < part2) return -1;
    }

    return 0;
  };

  const loadClickwrap = async (): Promise<void> => {
    try {
      const headers = {
        access_token: localStorage.getItem("@UltragazEmpresarial:accessToken"),
      };

      const { data } = await api.get<ClickwrapData[]>(
        "/industrial/customer/v1/clickwraps/active",
        {
          params: { fileType: "TERMO_ADESAO_PORTAL_ULTRAGAZ" },
          headers,
        }
      );

      if (data && data.length > 0) {
        const activeClickwraps = data.filter(
          (item) => item.status === "active"
        );

        if (activeClickwraps.length > 0) {
          const latestClickwrap = activeClickwraps.reduce((prev, current) => {
            const prevVersion = String(prev.versionNumber);
            const currentVersion = String(current.versionNumber);

            return compareVersions(currentVersion, prevVersion) > 0
              ? current
              : prev;
          });

          setClickwrap(latestClickwrap);
        }
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        toast.error("Termo de adesão não encontrado");
      } else if (err.response?.status !== 401) {
        toast.error("Erro ao carregar os termos de adesão");
      }
      // ignore
    }
  };

  const handleAgreed = async (): Promise<void> => {
    if (agreedHandledRef.current) {
      return;
    }
    agreedHandledRef.current = true;

    try {
      if (handleAccepted) {
        handleAccepted();
      }

      try {
        const localUserId = localStorage.getItem("@UltragazEmpresarial:userId");
        const clientUserId = customClientUserId || localUserId || "unknown";
        const agreedKey = `@UGZ:clickwrap:agreed:${clientUserId}`;
        localStorage.setItem(agreedKey, String(Date.now()));
        window.dispatchEvent(
          new CustomEvent("ugz-clickwrap-agreed", {
            detail: { clientUserId },
          })
        );
      } catch (e) {
        // ignore
      }

      // Fecha o modal após aceite
      setShowModal(false);
    } catch (err) {
      // ignore
    }
  };

  const handleDeclined = (): void => {
    toast.warn("Você precisa aceitar o termo de adesão para continuar");
  };

  const initializeDocuSign = (): void => {
    if (isInitialized) {
      return;
    }

    const container = document.getElementById("docusign-clickwrap-container");
    if (container && container.children.length > 0) {
      setIsInitialized(true);
      return;
    }

    if (!window.docuSignClick) {
      toast.error(
        "SDK DocuSign não carregado. Por favor, recarregue a página."
      );
      return;
    }

    if (!clickwrap?.clickwrapId) {
      toast.error("Configuração do termo não encontrada");
      return;
    }

    if (!container) {
      toast.error("Erro ao encontrar o container do termo");
      return;
    }

    if (!docusignConfig?.accountId || !docusignConfig?.environment) {
      toast.error("Configuração do DocuSign não encontrada");
      return;
    }

    const customerName = localStorage.getItem(
      "@UltragazEmpresarial:customerName"
    );
    const customerDocument = localStorage.getItem(
      "@UltragazEmpresarial:document"
    );

    if (!customerName || !customerDocument) {
      toast.error("Dados do cliente não encontrados");
      return;
    }

    try {
      cleanupDocuSign();

      if (container) {
        container.innerHTML = "";
      }

      const localUserId = localStorage.getItem("@UltragazEmpresarial:userId");

      const clientUserId = customClientUserId || localUserId;

      const userEmail =
        localStorage.getItem("@UltragazEmpresarial:loginEmail") || "";

      if (!userEmail) {
        toast.error("Email do usuário não encontrado");
        return;
      }

      const normalizedEnv = (docusignConfig.environment || "").replace(
        /\/clickapi\/?$/,
        ""
      );

      const documentData: {
        fullName: string;
        cpf: string;
        unityId?: string;
      } = {
        fullName: customerName || "",
        cpf: customerDocument || "",
      };

      if (unityId) {
        documentData.unityId = String(unityId);
      }

      const config = {
        environment: normalizedEnv || "https://demo.docusign.net",
        accountId: docusignConfig.accountId,
        clickwrapId: clickwrap.clickwrapId,
        clientUserId,
        documentData,
        onMustAgree: () => {
          setShowModal(true);
        },
        onAgreed: () => {
          handleAgreed();
        },
        onDeclined: () => {
          handleDeclined();
        },
        onError: () => {
          toast.error("Erro ao processar termo de adesão");
        },
      };

      if (!window.docuSignClick?.Clickwrap?.render) {
        toast.error("Método render do DocuSign não disponível");
        return;
      }

      if (!container || container.children.length === 0) {
        window.docuSignClick.Clickwrap.render(
          config,
          "#docusign-clickwrap-container"
        );
        setIsInitialized(true);
      } else {
        setIsInitialized(true);
      }
    } catch (error: any) {
      // ignore

      const errorMessage =
        error?.message ||
        "Erro desconhecido ao carregar o termo. Verifique o console para mais detalhes.";

      toast.error(errorMessage);
    }
  };

  return (
    <>
      {/* Backdrop transparente - sempre presente quando está renderizando, fica opaco quando modal aparece */}
      {shouldRender && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: showModal ? "rgba(0, 0, 0, 0.5)" : "transparent",
            zIndex: 999999999998,
            transition: "background-color 0.3s ease",
            pointerEvents: showModal ? "auto" : "none",
          }}
        />
      )}

      {/* Modal customizado - sempre montado; fica invisível até onMustAgree */}
      {shouldRender && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: "white",
            borderRadius: "10px",
            padding: "30px",
            maxWidth: "800px",
            width: "90%",
            maxHeight: "90vh",
            overflow: "auto",
            zIndex: 999999999999,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
            visibility: showModal ? "visible" : "hidden",
            pointerEvents: showModal ? "auto" : "none",
          }}
        >
          {/* Container do DocuSign - renderiza sempre aqui */}
          <div
            id="docusign-clickwrap-container"
            style={{
              minHeight: "400px",
              width: "100%",
              display: "block",
            }}
          />
        </div>
      )}
    </>
  );
};

export default TermoAdesao;
